/**
 * Enriquecimento de cadastro com IA (OpenRouter) — por produto, sob demanda, para SITE ou MARKETPLACE.
 *
 * Fluxo deliberadamente humano: a IA gera um RASCUNHO a partir dos fatos já cadastrados e lista em
 * "Faltando no cadastro" o que não pôde supor; nada vai ao ar sozinho. Aprovar um rascunho de SITE grava só na
 * camada comercial da Plataforma (nome, descrições, SEO, destaques) e NÃO publica o produto; rascunho de
 * MARKETPLACE fica guardado e aprovado para o módulo de marketplace consumir.
 *
 * Como em CommercialEditForm, a ação entra por prop (`ai`), não por hook: a tela é renderizável em teste
 * sem React Query. Sem o handler a seção diz que a ação não está montada em vez de oferecer botão morto.
 */
import React, { useState, type ReactElement } from "react";
import type { AiEnrichment, AiEnrichmentTarget } from "../api/ai-types.js";

export interface AiEnrichmentHandlers {
  drafts: AiEnrichment[];
  generate: (target: AiEnrichmentTarget, guidance: string) => void;
  resolve: (id: string, action: "approve" | "discard") => void;
  pending: boolean;
  error: Error | null;
}

const TARGET_LABEL: Record<AiEnrichmentTarget, string> = { site: "Site da loja", marketplace: "Marketplace" };
const STATUS_LABEL = { draft: "Rascunho", approved: "Aprovado", discarded: "Descartado" } as const;

/** Mensagens legíveis para as recusas conhecidas do servidor; o resto vira texto genérico (sem vazar detalhe interno). */
function errorText(error: Error): string {
  if (error.message.startsWith("openrouter_not_configured")) return "A chave do OpenRouter não está configurada (Configurações › IA).";
  if (error.message.startsWith("ai_owner_only") || error.message.includes(":403")) return "Seu usuário não tem acesso ao módulo de catálogo.";
  return "Não foi possível concluir a ação agora. Tente de novo.";
}

export function AiEnrichmentPanel({ ai }: { ai?: AiEnrichmentHandlers }): ReactElement {
  const [target, setTarget] = useState<AiEnrichmentTarget>("site");
  const [guidance, setGuidance] = useState("");
  return (
    <section className="product-block flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-block="ia-enriquecimento">
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-ink">Enriquecer cadastro com IA</h2>
        <p className="text-sm text-muted">
          A IA reescreve e organiza só o que já está cadastrado e aponta o que falta. Nada é publicado sem a sua aprovação.
        </p>
      </header>

      {!ai ? (
        <p className="text-sm text-muted" data-state="not-mounted">O enriquecimento com IA não está montado nesta visualização.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3" data-form="ia-enriquecimento">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink">Destino</span>
              <select className="rounded-control border border-border bg-surface px-2 py-1.5" value={target} onChange={(event) => setTarget(event.target.value as AiEnrichmentTarget)}>
                <option value="site">{TARGET_LABEL.site}</option>
                <option value="marketplace">{TARGET_LABEL.marketplace}</option>
              </select>
            </label>
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm">
              <span className="font-medium text-ink">Orientação (opcional)</span>
              <input className="rounded-control border border-border bg-surface px-2 py-1.5" maxLength={400} value={guidance} onChange={(event) => setGuidance(event.target.value)} placeholder="Ex.: foco em quem está começando" />
            </label>
            <button type="button" data-action="ia-gerar" className="rounded-control bg-brand px-3 py-2 text-sm font-semibold text-white disabled:opacity-60" disabled={ai.pending} onClick={() => ai.generate(target, guidance)}>
              {ai.pending ? "Gerando…" : "Gerar rascunho"}
            </button>
          </div>
          {ai.error ? <p role="alert" className="text-sm text-danger">{errorText(ai.error)}</p> : null}

          {ai.drafts.length === 0 ? (
            <p className="text-sm text-muted">Nenhum rascunho gerado para este produto ainda.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {ai.drafts.map((item) => (
                <li key={item.id} className="flex flex-col gap-2 rounded-control border border-border p-3" data-enrichment-status={item.status}>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                    <strong className="text-ink">{TARGET_LABEL[item.target]}</strong>
                    <span>{STATUS_LABEL[item.status]}</span>
                    <span>{new Date(item.created_at).toLocaleString("pt-BR")}</span>
                    {item.created_by ? <span>por {item.created_by}</span> : null}
                  </div>
                  <p className="text-sm font-semibold text-ink">{item.draft.title}</p>
                  <p className="text-sm text-ink">{item.draft.shortDescription}</p>
                  <p className="whitespace-pre-line text-sm text-ink">{item.draft.description}</p>
                  {item.draft.bullets.length ? (
                    <ul className="list-disc pl-5 text-sm text-ink">{item.draft.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>
                  ) : null}
                  <p className="text-xs text-muted">SEO: {item.draft.seoTitle} — {item.draft.seoDescription}</p>
                  {item.draft.missing.length ? (
                    <p className="text-sm text-warning" data-field="faltando">Faltando no cadastro: {item.draft.missing.join(", ")}</p>
                  ) : null}
                  {item.status === "draft" ? (
                    <div className="flex gap-2">
                      <button type="button" data-action="ia-aprovar" className="rounded-control bg-brand px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60" disabled={ai.pending} onClick={() => ai.resolve(item.id, "approve")}>
                        {item.target === "site" ? "Aprovar e aplicar ao cadastro" : "Aprovar para o marketplace"}
                      </button>
                      <button type="button" data-action="ia-descartar" className="rounded-control border border-border px-3 py-1.5 text-sm disabled:opacity-60" disabled={ai.pending} onClick={() => ai.resolve(item.id, "discard")}>
                        Descartar
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
